<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>View Violation Details</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <!-- Filter Dropdown -->
        <div class="row mb-3">
            <div class="col-md-4">
                <div class="form-group">
                    <label for="filterViolation">Filter by Violation</label>
                    <select id="filterViolation" v-model="selectedFilter" class="form-control">
                        <option value="">All Violations</option>
                        <option v-for="violation in violations" :key="violation.id" :value="violation.id">
                            {{ violation.title }}
                        </option>
                    </select>
                </div>
            </div>
        </div>

        <!-- Table -->
        <div v-if="hasViewPermission" class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6>Violation Details List <br><br><strong>You have {{ violationParameters.length }}
                                    registered so far!</strong></h6>
                            <router-link to="/violation/parameter/add"
                                class="btn btn-primary d-flex align-items-center">
                                Add Violation Detail
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="feather feather-plus ms-2">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </router-link>
                        </div>
                        <div class="table-responsive">
                            <div v-if="loading" class="text-center py-4">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                            </div>

                            <template v-else>
                                <table class="table table-hover table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Data Type</th>
                                            <th>Active Status</th>
                                            <th class="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in filteredViolationParameters" :key="item.id">
                                            <td>{{ item.name }}</td>
                                            <td>{{ item.parameter_type.type }}</td>
                                            <td class="text-center">
                                                <span :class="item.is_active ? 'text-success' : 'text-danger'">
                                                    {{ item.is_active ? 'Active' : 'Inactive' }}
                                                </span>
                                            </td>
                                            <td class="text-center">
                                                <!-- Edit Button -->
                                                <button v-if="hasEditPermission" @click="goToEdit(item.id)" class="btn btn-primary btn-sm me-2">
                                                    Edit
                                                </button>
                                                <!-- Delete Button -->
                                                <button v-if="hasDeletePermission" @click="deleteViolationParameter(item.id)"
                                                    class="btn btn-danger btn-sm mt-1">
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                        <tr v-if="filteredViolationParameters.length === 0">
                                            <td colspan="4" class="text-center text-muted">
                                                No violation Details found
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}
</style>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useMeta } from "@/composables/use-meta";
import axios from 'axios';
import { BASE_URL } from '@/Config/config';
import Swal from 'sweetalert2';
import { useStore } from 'vuex';

const router = useRouter();
useMeta({ title: "View Violation Parameters" });

const store = useStore();

// Reactive data
const violationParameters = ref([]);
const violations = ref([]);
const selectedFilter = ref("");
const loading = ref(false);

// SweetAlert configuration
const showConfirmDialog = async (title, text) => {
    return await Swal.fire({
        title: title,
        text: text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Delete',
        padding: '2em'
    });
};

const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6'
    });
};

// Permission Checks
const hasViewPermission = computed(() => store.getters.hasPermission('view_violation_parameter'));
const hasCreatePermission = computed(() => store.getters.hasPermission('create_violation_parameter'));
const hasEditPermission = computed(() => store.getters.hasPermission('edit_violation_parameter'));
const hasDeletePermission = computed(() => store.getters.hasPermission('delete_violation_parameter'));

// Load data on mount
onMounted(async () => {
    loading.value = true;
    try {
        await loadViolations();
    } catch (error) {
        console.error('Error loading data:', error);
        showErrorAlert('Failed to load data. Please try again later.');
    } finally {
        loading.value = false;
    }
});

// Load violations and extract parameters
const loadViolations = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/violations`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violations.value = response.data;

        // Flatten all parameters from all violations into one array
        violationParameters.value = response.data.flatMap(violation =>
            (violation.parameters || []).map(param => ({
                ...param,
                violation_title: violation.title
            }))
        );
    } catch (error) {
        console.error('Error loading violations:', error);
        throw error;
    }
};

// Filter parameters based on selected violation
const filteredViolationParameters = computed(() => {
    if (!selectedFilter.value) return violationParameters.value;
    return violationParameters.value.filter(
        param => Number(param.violation_id) === Number(selectedFilter.value))
});

// Navigate to edit page
const goToEdit = (id) => {
    router.push(`/violation/parameter/edit/${id}`);
};

// Delete violation parameter with confirmation
const deleteViolationParameter = async (id) => {
    const result = await showConfirmDialog(
        'Are you sure?',
        'You won\'t be able to revert this!'
    );

    if (result.isConfirmed) {
        try {
            await axios.delete(`${BASE_URL}/violation-parameters/${id}`, {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            });
            violationParameters.value = violationParameters.value.filter(p => p.id !== id);
            showSuccessAlert('Violation detail deleted successfully');
        } catch (error) {
            console.error('Error deleting violation detail:', error);
            let errorMessage = 'Failed to delete violation detail';

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.request) {
                errorMessage = 'Network error - please check your connection';
            }

            showErrorAlert(errorMessage);
        }
    }
};
</script>